import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LuPlus, LuTrash2, LuPencil, LuCheck, LuX, LuEye, LuMessageSquare, LuSend, LuUserCog, LuSmartphone, LuDatabase, LuCalendar, LuShield, LuUser, LuSave, LuCamera, LuUserPlus, LuChevronDown, LuChevronRight, LuCpu, LuServer, LuBuilding, LuAward, LuFolder, LuHouse, LuMapPin } from 'react-icons/lu'
import { FiMessageCircle, FiTrendingUp } from 'react-icons/fi'
import { WhatsAppIcon, MetaIcon } from './DevicesPanel'

interface User {
  id: string
  username: string
  email: string | null
  full_name: string | null
  is_active: boolean
  is_admin: boolean
  created_at: string
  last_login: string | null
  login_count: number
  avatar_url?: string | null
}

interface Device {
  id: string
  name: string
  phone_number?: string
  display_phone?: string
  status?: string
  api_type?: 'evolution' | 'meta'
  profile_pic_url?: string
}

interface DevicePermission {
  device_id: string
  device_name: string
  can_view: boolean
  can_chat: boolean
  can_edit: boolean
  can_blast: boolean
  can_admin: boolean
}

interface DevicePermissionUpdate {
  canView?: boolean
  canChat?: boolean
  canEdit?: boolean
  canBlast?: boolean
  canAdmin?: boolean
}

interface ToolPermission {
  tool_name: string
  can_access: boolean
  can_create: boolean
  can_edit: boolean
  can_delete: boolean
}

interface FolderGrant {
  folderPath: string
  recursive: boolean
  canUpload: boolean
}

interface UserPermissions {
  user: {
    id: string
    username: string
    fullName: string
    isAdmin: boolean
  }
  isAdmin: boolean
  devicePermissions: DevicePermission[]
  toolPermissions: ToolPermission[]
  accessibleDevices: Device[]
  accessibleTools: string[]
  folderGrants?: FolderGrant[]
}

const AVAILABLE_TOOLS = [
  { name: 'whatsapp', label: 'Conversas WhatsApp', icon: LuMessageSquare },
  { name: 'mapa', label: 'Mapa de Ativas', icon: LuMapPin },
  { name: 'blast', label: 'Marketing', icon: LuSend },
  { name: 'rag', label: 'Base de Conhecimento', icon: LuDatabase },
  { name: 'integrations', label: 'Integrações', icon: FiMessageCircle },
  { name: 'schedule', label: 'Agendamento', icon: LuCalendar },
  { name: 'settings', label: 'Configurações', icon: LuUserCog },
  { name: 'infrastructure', label: 'Dados', icon: LuServer },
  { name: 'sistema', label: 'Tecnologia', icon: LuCpu },
  { name: 'leaders', label: 'Recrutamento', icon: LuAward },
  // App Luísa (28/09/2026): ver = dados do app; agir = senha, bloqueio, acesso; loja = trocar a revista.
  { name: 'aplicativo', label: 'Aplicativo — ver', icon: LuSmartphone },
  { name: 'aplicativo_gestao', label: 'Aplicativo — agir (senha, bloqueio, acesso)', icon: LuSmartphone },
  { name: 'loja', label: 'Aplicativo — trocar a revista', icon: LuSmartphone },
  // Criativos (01/10/2026): ver é uma permissão; enviar/criar pasta/renomear/apagar é outra.
  { name: 'criativos', label: 'Criativos — ver', icon: LuSmartphone },
  { name: 'criativos_gestao', label: 'Criativos — enviar e organizar as artes', icon: LuSmartphone },
]

// Aplicativos externos do ecossistema (fin.twms etc.). Persistem na MESMA tabela
// das ferramentas (user_tool_permissions), só com outro tool_name — por isso o
// toggle reusa handleToolPermission. Lista separada de AVAILABLE_TOOLS de
// propósito: aplicativo não é ferramenta interna do CRM, e aqui NÃO existe
// bypass de admin (o backend do app destino barra com 403 quem não tem a
// permissão explícita, mesmo administradores).
const AVAILABLE_APPS = [
  { name: 'app_financeiro', label: 'SCF — Financeiro (fin.twms)', icon: FiTrendingUp },
]

// Toast notification component
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-slide-up ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? <LuCheck className="w-4 h-4" /> : <LuX className="w-4 h-4" />}
      <span className="text-sm">{message}</span>
    </div>
  )
}

interface SettingsPanelProps {
  onOpenPermissions?: (user: User, permissions: UserPermissions, devices: Device[]) => void
}

export function SettingsPanel({ onOpenPermissions }: SettingsPanelProps) {
  const { authenticatedFetch } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState<'users' | 'internal_contacts' | null>('users')

  // Toggle section - clica expande, clica retrai
  const toggleSection = (section: 'users' | 'internal_contacts') => {
    setActiveSection(prev => prev === section ? null : section)
  }

  // Contatos internos pra encaminhamento (depósito, financeiro, líderes, etc.)
  const [internalContacts, setInternalContacts] = useState<Array<{ id: string; name: string; phone: string; category: string | null }>>([])
  const [editingContactId, setEditingContactId] = useState<string | null>(null)
  const [contactDraft, setContactDraft] = useState<{ name: string; phone: string; category: string }>({ name: '', phone: '', category: '' })
  const [savingContact, setSavingContact] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)

  const loadInternalContacts = useCallback(async () => {
    try {
      const r = await authenticatedFetch('/api/internal-contacts')
      const d = await r.json()
      setInternalContacts(d.contacts || [])
    } catch { /* ignore */ }
  }, [authenticatedFetch])

  useEffect(() => {
    if (activeSection === 'internal_contacts') loadInternalContacts()
  }, [activeSection, loadInternalContacts])

  const resetContactDraft = () => {
    setEditingContactId(null)
    setContactDraft({ name: '', phone: '', category: '' })
    setContactError(null)
  }

  const startEditContact = (c: { id: string; name: string; phone: string; category: string | null }) => {
    setEditingContactId(c.id)
    setContactDraft({ name: c.name, phone: c.phone, category: c.category || '' })
    setContactError(null)
  }

  const saveContact = async () => {
    if (!contactDraft.name.trim() || !contactDraft.phone.trim()) {
      setContactError('Nome e telefone são obrigatórios')
      return
    }
    setSavingContact(true); setContactError(null)
    try {
      const body = {
        name: contactDraft.name.trim(),
        phone: contactDraft.phone.trim(),
        category: contactDraft.category.trim() || null,
      }
      const r = editingContactId
        ? await authenticatedFetch(`/api/internal-contacts/${editingContactId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
          })
        : await authenticatedFetch('/api/internal-contacts', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
          })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Erro ao salvar')
      await loadInternalContacts()
      resetContactDraft()
    } catch (err: any) {
      setContactError(err.message)
    } finally {
      setSavingContact(false)
    }
  }

  const deleteContact = async (id: string) => {
    if (!confirm('Excluir este contato interno?')) return
    try {
      await authenticatedFetch(`/api/internal-contacts/${id}`, { method: 'DELETE' })
      await loadInternalContacts()
      if (editingContactId === id) resetContactDraft()
    } catch { /* ignore */ }
  }

  const loadUsers = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/admin/users')
      if (!response.ok) throw new Error('Erro')
      const data = await response.json()
      if (data.success) setUsers(data.users)
    } catch { setError('Erro ao carregar') }
  }, [authenticatedFetch])

  const loadDevices = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/whatsapp-devices')
      if (!response.ok) return
      const data = await response.json()
      setDevices(Array.isArray(data) ? data : data.devices || [])
    } catch { /* ignore */ }
  }, [authenticatedFetch])

  useEffect(() => {
    Promise.all([loadUsers(), loadDevices()]).then(() => setLoading(false))
  }, [loadUsers, loadDevices])

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Excluir este usuário?')) return
    try {
      const response = await authenticatedFetch(`/api/admin/users/${userId}`, { method: 'DELETE' })
      const data = await response.json()
      if (data.success) loadUsers()
      else setError(data.error)
    } catch { setError('Erro ao excluir') }
  }

  const openPermissionsPanel = async (user: User) => {
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.id}/permissions`)
      if (!response.ok) return
      const data = await response.json()
      if (data.success && onOpenPermissions) {
        onOpenPermissions(user, data.permissions, devices)
      }
    } catch { /* ignore */ }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-24"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>
  }

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="mx-3 mb-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-md text-red-500 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="hover:bg-red-500/20 rounded p-1"><LuX className="w-4 h-4" /></button>
        </div>
      )}

      {/* Seções expansíveis */}
      <div className="flex-1 flex flex-col gap-1 overflow-hidden p-2">
        {/* SEÇÃO: Usuários */}
        <div className={`neu-flat rounded border border-border transition-all ${activeSection === 'users' ? 'flex-1 flex flex-col' : 'flex-shrink-0'}`}>
          <button
            onClick={() => toggleSection('users')}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium ${
              activeSection === 'users' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50 text-muted-foreground'
            }`}
          >
            {activeSection === 'users' ? <LuChevronDown className="w-4 h-4" /> : <LuChevronRight className="w-4 h-4" />}
            <LuUser className="w-4 h-4" />
            <span>Usuários</span>
            <span className="ml-auto text-xs bg-primary/20 text-primary px-1.5 rounded">{users.length}</span>
          </button>

          {activeSection === 'users' && (
            <div className="flex-1 overflow-auto p-2 border-t border-border">
              {/* Grid de cards 2 colunas - compacto */}
              <div className="grid grid-cols-2 gap-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="neu-card flex items-center gap-2.5 p-2 bg-card border border-border rounded-lg hover:border-primary/30 hover:shadow-sm transition-all"
                  >
                    {/* Avatar compacto */}
                    <div className="relative flex-shrink-0">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold ${user.is_admin ? 'bg-amber-500' : 'bg-blue-500'}`}>
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${user.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                    </div>

                    {/* Info compacta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold truncate">{user.username}</span>
                        {!!user.is_admin && <LuShield className="w-3 h-3 text-amber-500 flex-shrink-0" title="Administrador" />}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {user.full_name && (
                          <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">{user.full_name}</span>
                        )}
                        {user.is_admin ? (
                          <span className="text-[9px] bg-amber-500/20 text-amber-600 dark:text-amber-400 px-1 py-0.5 rounded font-medium">Admin</span>
                        ) : (
                          <span className="text-[9px] bg-blue-500/20 text-blue-600 dark:text-blue-400 px-1 py-0.5 rounded font-medium">Usuário</span>
                        )}
                        {!user.is_active && (
                          <span className="text-[9px] bg-red-500/20 text-red-500 px-1 py-0.5 rounded font-medium">Inativo</span>
                        )}
                      </div>
                    </div>

                    {/* Botões de ação compactos */}
                    {user.username !== 'cleverson.pompeu' && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {onOpenPermissions && (
                          <button
                            onClick={() => openPermissionsPanel(user)}
                            className="p-1.5 hover:bg-primary/10 rounded-md text-muted-foreground hover:text-primary transition-colors"
                            title="Editar usuário"
                          >
                            <LuPencil className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-1.5 hover:bg-red-500/10 rounded-md text-red-500/70 hover:text-red-500 transition-colors"
                          title="Excluir usuário"
                        >
                          <LuTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Empty state */}
              {users.length === 0 && (
                <div className="text-center py-12">
                  <LuUser className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground text-sm">Nenhum usuário cadastrado</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* SEÇÃO: Contatos Internos */}
        <div className={`neu-flat rounded border border-border transition-all ${activeSection === 'internal_contacts' ? 'flex-1 flex flex-col min-h-0' : 'flex-shrink-0'}`}>
          <button
            onClick={() => toggleSection('internal_contacts')}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium ${
              activeSection === 'internal_contacts' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50 text-muted-foreground'
            }`}
          >
            {activeSection === 'internal_contacts' ? <LuChevronDown className="w-4 h-4" /> : <LuChevronRight className="w-4 h-4" />}
            <LuBuilding className="w-4 h-4" />
            <span>Contatos Internos</span>
            <span className="ml-auto text-xs bg-primary/20 text-primary px-1.5 rounded">{internalContacts.length}</span>
            <span className="text-[10px] text-muted-foreground/60 font-normal hidden sm:inline">
              · pra encaminhar mensagens (depósito, financeiro, etc.)
            </span>
          </button>

          {activeSection === 'internal_contacts' && (() => {
            // Agrupa por categoria pra renderizar em seções; sem categoria vira "Outros".
            const grouped: Record<string, typeof internalContacts> = {}
            for (const c of internalContacts) {
              const k = c.category?.trim() || 'Outros'
              if (!grouped[k]) grouped[k] = []
              grouped[k].push(c)
            }
            // Cor consistente derivada do hash do nome — mesma inicial sempre = mesma cor.
            const avatarColors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-purple-500', 'bg-sky-500', 'bg-orange-500', 'bg-teal-500']
            const colorOf = (name: string) => {
              let h = 0
              for (let i = 0; i < name.length; i++) h = ((h << 5) - h) + name.charCodeAt(i)
              return avatarColors[Math.abs(h) % avatarColors.length]
            }
            const fmtPhone = (raw: string) => {
              const d = raw.replace(/\D/g, '')
              if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`
              if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`
              return raw
            }
            return (
            <div className="flex-1 overflow-auto p-3 border-t border-border space-y-4">
              {internalContacts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <LuBuilding className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Nenhum contato interno cadastrado ainda</p>
                  <p className="text-[10px] mt-1">Adicione no card abaixo ou pelo modal de encaminhamento</p>
                </div>
              ) : (
                /* Cards agrupados por categoria */
                Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, list]) => (
                  <div key={cat}>
                    <div className="flex items-baseline gap-2 mb-2 px-1">
                      <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{cat}</h4>
                      <span className="text-[10px] text-muted-foreground/60">·</span>
                      <span className="text-[10px] text-muted-foreground/70">{list.length} contato{list.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {list.map(c => {
                        const initial = c.name.trim().charAt(0).toUpperCase()
                        const isEditing = editingContactId === c.id
                        return (
                          <div
                            key={c.id}
                            className={`group relative flex items-center gap-2.5 p-2.5 rounded-lg border transition-all ${
                              isEditing
                                ? 'border-primary/50 bg-primary/5 shadow-sm'
                                : 'neu-card border-border bg-card hover:border-primary/30 hover:shadow-sm'
                            }`}
                          >
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0 ${colorOf(c.name)}`}>
                              {initial}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-foreground truncate">{c.name}</div>
                              <div className="text-[10px] text-muted-foreground tabular-nums truncate">{fmtPhone(c.phone)}</div>
                            </div>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => startEditContact(c)}
                                className="p-1.5 hover:bg-primary/10 rounded text-muted-foreground hover:text-primary transition-colors"
                                title="Editar contato"
                              >
                                <LuPencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteContact(c.id)}
                                className="p-1.5 hover:bg-red-500/10 rounded text-red-500/70 hover:text-red-500 transition-colors"
                                title="Excluir contato"
                              >
                                <LuTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))
              )}

              {/* Card de adicionar/editar — fica sempre visível, foco vem aqui ao clicar edit */}
              <div className={`rounded-lg border-2 ${editingContactId ? 'border-primary bg-primary/5' : 'neu-card border-dashed border-border bg-card/30'} p-3`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${editingContactId ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {editingContactId ? <LuPencil className="w-3.5 h-3.5" /> : <LuPlus className="w-4 h-4" />}
                  </div>
                  <h4 className="text-xs font-semibold text-foreground">
                    {editingContactId ? 'Editar contato' : 'Adicionar contato'}
                  </h4>
                  {editingContactId && (
                    <button
                      onClick={resetContactDraft}
                      className="ml-auto p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                      title="Cancelar edição"
                    >
                      <LuX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={contactDraft.name}
                    onChange={e => setContactDraft(d => ({ ...d, name: e.target.value }))}
                    placeholder="Nome (ex: Depósito)"
                    className="px-2.5 py-1.5 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <input
                    type="text"
                    value={contactDraft.phone}
                    onChange={e => setContactDraft(d => ({ ...d, phone: e.target.value }))}
                    placeholder="Telefone (DDD+número)"
                    className="px-2.5 py-1.5 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <input
                    type="text"
                    value={contactDraft.category}
                    onChange={e => setContactDraft(d => ({ ...d, category: e.target.value }))}
                    placeholder="Categoria (Depósito, Financeiro…)"
                    className="px-2.5 py-1.5 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                {contactError && (
                  <div className="mt-2 text-[11px] text-red-500">{contactError}</div>
                )}
                <div className="flex justify-end mt-2">
                  <button
                    onClick={saveContact}
                    disabled={savingContact || !contactDraft.name.trim() || !contactDraft.phone.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors disabled:opacity-50"
                  >
                    <LuSave className="w-3 h-3" />
                    {savingContact ? 'Salvando...' : (editingContactId ? 'Atualizar' : 'Cadastrar')}
                  </button>
                </div>
              </div>
            </div>
            )
          })()}
        </div>

        {/* SEÇÃO: Configurar LLM - removida (gerenciado pelo backend) */}
      </div>
    </div>
  )
}

// Helper function to get initials
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Painel completo de configuração do usuário (abre no flip)
export function UserPermissionsPanel({
  user,
  permissions,
  devices,
  onClose,
  authenticatedFetch,
  onUserUpdated
}: {
  user: User
  permissions: UserPermissions
  devices: Device[]
  onClose: () => void
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
  onUserUpdated?: () => void
}) {
  const [localPerms, setLocalPerms] = useState(permissions)
  // Gate da seção APLICATIVOS: só quem TEM o grant `app_financeiro` enxerga e
  // gerencia o acesso aos aplicativos ("quem tem acesso ao financeiro gerencia
  // o acesso ao financeiro"). Ser admin NÃO basta — existe admin sem acesso ao
  // fin, e esse não deve nem ver a seção. Sem hardcode de usernames: a regra é
  // o próprio grant. O backend valida do lado dele; isto aqui é só a UI.
  const { user: loggedUser } = useAuth()
  const canManageApps = !!loggedUser?.permissions?.toolPermissions?.some(
    t => t.tool_name === 'app_financeiro' && !!t.can_access
  )
  const [userData, setUserData] = useState({
    fullName: user.full_name || '',
    email: user.email || '',
    isActive: user.is_active,
    isAdmin: user.is_admin,
    password: '',
    avatarUrl: user.avatar_url || ''
  })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }

  // Salvar dados do usuário
  const handleSaveUser = async () => {
    setSaving(true)
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          fullName: userData.fullName,
          email: userData.email,
          isActive: userData.isActive,
          isAdmin: userData.isAdmin,
          avatarUrl: userData.avatarUrl,
          ...(userData.password ? { password: userData.password } : {})
        })
      })
      const data = await response.json()
      if (data.success) {
        showToast('Dados salvos com sucesso', 'success')
        if (onUserUpdated) onUserUpdated()
      } else {
        showToast(data.error || 'Erro ao salvar', 'error')
      }
    } catch {
      showToast('Erro ao salvar dados', 'error')
    }
    setSaving(false)
  }

  // Handle avatar upload
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      showToast('Imagem muito grande (máx. 500KB)', 'error')
      return
    }

    // Convert to base64
    const reader = new FileReader()
    reader.onload = async (event) => {
      const base64 = event.target?.result as string
      setUserData(prev => ({ ...prev, avatarUrl: base64 }))

      // Auto-save avatar
      try {
        const response = await authenticatedFetch(`/api/admin/users/${user.id}/avatar`, {
          method: 'POST',
          body: JSON.stringify({ avatar: base64 })
        })
        const data = await response.json()
        if (data.success) {
          showToast('Foto atualizada', 'success')
          if (onUserUpdated) onUserUpdated()
        }
      } catch {
        showToast('Erro ao enviar foto', 'error')
      }
    }
    reader.readAsDataURL(file)
  }

  const handleToolPermission = async (toolName: string, canAccess: boolean) => {
    try {
      await authenticatedFetch(`/api/admin/users/${user.id}/tool-permissions`, {
        method: 'POST',
        body: JSON.stringify({ toolName, canAccess, canCreate: canAccess, canEdit: canAccess, canDelete: canAccess })
      })
      const newToolPerms = [...localPerms.toolPermissions]
      const idx = newToolPerms.findIndex(p => p.tool_name === toolName)
      if (idx >= 0) {
        newToolPerms[idx] = { ...newToolPerms[idx], can_access: canAccess }
      } else {
        newToolPerms.push({ tool_name: toolName, can_access: canAccess, can_create: canAccess, can_edit: canAccess, can_delete: canAccess })
      }
      setLocalPerms({ ...localPerms, toolPermissions: newToolPerms })
      showToast(`Ferramenta ${canAccess ? 'liberada' : 'removida'}`, 'success')
    } catch {
      showToast('Erro ao atualizar permissão', 'error')
    }
  }

  // ── Concessões de pasta (explorer de Arquivos) ──────────────────────────────
  const folderGrants = localPerms.folderGrants || []
  // POST faz upsert; usado tanto para adicionar quanto para alternar recursivo/envio.
  const saveFolderGrant = async (g: FolderGrant) => {
    try {
      const r = await authenticatedFetch(`/api/admin/users/${user.id}/folder-access`, {
        method: 'POST', body: JSON.stringify(g),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || d.error) throw new Error(d.error || 'erro')
      const next = folderGrants.filter(x => x.folderPath !== g.folderPath)
      next.push(g)
      next.sort((a, b) => a.folderPath.localeCompare(b.folderPath))
      setLocalPerms({ ...localPerms, folderGrants: next })
      showToast(`Pasta liberada: ${g.folderPath}`, 'success')
    } catch {
      showToast('Erro ao liberar pasta', 'error')
    }
  }
  const removeFolderGrant = async (folderPath: string) => {
    try {
      await authenticatedFetch(`/api/admin/users/${user.id}/folder-access?folderPath=${encodeURIComponent(folderPath)}`, { method: 'DELETE' })
      setLocalPerms({ ...localPerms, folderGrants: folderGrants.filter(x => x.folderPath !== folderPath) })
      showToast('Acesso removido', 'success')
    } catch {
      showToast('Erro ao remover acesso', 'error')
    }
  }

  const handleDevicePermission = async (deviceId: string, perms: DevicePermissionUpdate | null) => {
    try {
      if (perms === null) {
        await authenticatedFetch(`/api/admin/users/${user.id}/device-permissions/${deviceId}`, { method: 'DELETE' })
        setLocalPerms({ ...localPerms, devicePermissions: localPerms.devicePermissions.filter(p => p.device_id !== deviceId) })
        showToast('Dispositivo removido', 'success')
      } else {
        await authenticatedFetch(`/api/admin/users/${user.id}/device-permissions`, {
          method: 'POST',
          body: JSON.stringify({ deviceId, ...perms })
        })
        const newDevicePerms = [...localPerms.devicePermissions]
        const idx = newDevicePerms.findIndex(p => p.device_id === deviceId)
        const device = devices.find(d => d.id === deviceId)
        const isNewDevice = idx < 0
        if (idx >= 0) {
          newDevicePerms[idx] = { ...newDevicePerms[idx], can_view: perms.canView ?? newDevicePerms[idx].can_view, can_chat: perms.canChat ?? newDevicePerms[idx].can_chat, can_edit: perms.canEdit ?? newDevicePerms[idx].can_edit, can_blast: perms.canBlast ?? newDevicePerms[idx].can_blast, can_admin: perms.canAdmin ?? newDevicePerms[idx].can_admin }
        } else {
          newDevicePerms.push({ device_id: deviceId, device_name: device?.name || '', can_view: perms.canView ?? true, can_chat: perms.canChat ?? true, can_edit: perms.canEdit ?? false, can_blast: perms.canBlast ?? false, can_admin: perms.canAdmin ?? false })
          showToast('Dispositivo adicionado', 'success')
        }

        // Auto-habilitar "Integrações" quando um dispositivo é adicionado
        let newToolPerms = [...localPerms.toolPermissions]
        if (isNewDevice) {
          const integrationsPerm = newToolPerms.find(p => p.tool_name === 'integrations')
          if (!integrationsPerm?.can_access) {
            // Habilitar permissão de Integrações automaticamente
            await authenticatedFetch(`/api/admin/users/${user.id}/tool-permissions`, {
              method: 'POST',
              body: JSON.stringify({ toolName: 'integrations', canAccess: true, canCreate: true, canEdit: true, canDelete: true })
            })
            const intIdx = newToolPerms.findIndex(p => p.tool_name === 'integrations')
            if (intIdx >= 0) {
              newToolPerms[intIdx] = { ...newToolPerms[intIdx], can_access: true }
            } else {
              newToolPerms.push({ tool_name: 'integrations', can_access: true, can_create: true, can_edit: true, can_delete: true })
            }
          }
        }

        setLocalPerms({ ...localPerms, devicePermissions: newDevicePerms, toolPermissions: newToolPerms })
      }
    } catch {
      showToast('Erro ao atualizar permissão', 'error')
    }
  }

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden neu-flat">
      {/* Toast notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleAvatarChange}
      />

      {/* Header com avatar, nome, controles e botão salvar */}
      <div className="neu-flat flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="relative cursor-pointer group"
          >
            {userData.avatarUrl ? (
              <img src={userData.avatarUrl} alt="" className="w-11 h-11 rounded-full object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-lg">
                {getInitials(user.full_name || user.username)}
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <LuCamera className="w-4 h-4 text-white" />
            </div>
          </div>
          <div>
            <div className="font-semibold text-base">{user.username}</div>
            <div className="text-sm text-muted-foreground">{user.full_name || 'Sem nome'}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={userData.isActive} onChange={(e) => setUserData({ ...userData, isActive: e.target.checked })} className="w-4 h-4 rounded" />
            Ativo
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={userData.isAdmin} onChange={(e) => setUserData({ ...userData, isAdmin: e.target.checked })} className="w-4 h-4 rounded" />
            Admin
          </label>
          <button
            onClick={handleSaveUser}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <LuSave className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-md"><LuX className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Dados do usuário - 3 inputs em linha */}
      <div className="px-4 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3">
          <LuUser className="w-4 h-4" />
          DADOS DO USUÁRIO
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Nome completo</label>
            <input
              type="text"
              value={userData.fullName}
              onChange={(e) => setUserData({ ...userData, fullName: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="Nome completo"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Email</label>
            <input
              type="email"
              value={userData.email}
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="email@exemplo.com"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Nova senha</label>
            <input
              type="password"
              value={userData.password}
              onChange={(e) => setUserData({ ...userData, password: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="Deixe vazio para manter"
            />
          </div>
        </div>
      </div>

      {/* Aplicativos - acesso aos outros apps do ecossistema (sem bypass de admin).
          Visível só pra quem já tem o grant app_financeiro (ver canManageApps). */}
      {canManageApps && (
      <div className="px-4 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-1">
          <LuShield className="w-4 h-4" />
          APLICATIVOS
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Admins não têm bypass aqui: sem a permissão, nem administrador entra no aplicativo (diferente das ferramentas).
        </p>
        <div className="flex gap-3 flex-wrap">
          {AVAILABLE_APPS.map((app) => {
            const perm = localPerms.toolPermissions.find(p => p.tool_name === app.name)
            const hasAccess = perm?.can_access || false
            const Icon = app.icon
            return (
              <button
                key={app.name}
                onClick={() => handleToolPermission(app.name, !hasAccess)}
                title={hasAccess ? `Remover acesso de ${user.username} ao ${app.label}` : `Liberar ${app.label} para ${user.username}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  hasAccess ? 'bg-green-500/20 text-green-600 border border-green-500/30' : 'bg-muted/50 text-muted-foreground border border-transparent hover:bg-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{app.label}</span>
              </button>
            )
          })}
        </div>
      </div>
      )}

      {/* Ferramentas - Tags horizontais */}
      <div className="px-4 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3">
          <LuSmartphone className="w-4 h-4" />
          FERRAMENTAS
        </div>
        <div className="flex gap-3 flex-wrap">
          {AVAILABLE_TOOLS.map((tool) => {
            const perm = localPerms.toolPermissions.find(p => p.tool_name === tool.name)
            const hasAccess = perm?.can_access || false
            const Icon = tool.icon
            return (
              <button
                key={tool.name}
                onClick={() => handleToolPermission(tool.name, !hasAccess)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  hasAccess ? 'bg-green-500/20 text-green-600 border border-green-500/30' : 'bg-muted/50 text-muted-foreground border border-transparent hover:bg-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tool.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Acesso a pastas (explorer de Arquivos) */}
      <div className="px-4 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <LuFolder className="w-4 h-4" />
            ACESSO A PASTAS
          </div>
          <button onClick={() => setPickerOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
            <LuPlus className="w-3.5 h-3.5" /> Liberar pasta
          </button>
        </div>
        {userData.isAdmin && (
          <p className="text-xs text-muted-foreground mb-2">Este usuário é administrador — já enxerga todas as pastas.</p>
        )}
        {folderGrants.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nenhuma pasta liberada.</p>
        ) : (
          <div className="space-y-2">
            {folderGrants.map(g => (
              <div key={g.folderPath} className="neu-card flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 text-sm">
                <LuFolder className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="flex-1 min-w-0 truncate font-mono text-xs" title={g.folderPath}>{g.folderPath}</span>
                <button onClick={() => saveFolderGrant({ ...g, recursive: !g.recursive })}
                  title={g.recursive ? 'Recursivo: inclui as subpastas' : 'Apenas esta pasta (sem subpastas)'}
                  className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${g.recursive ? 'bg-blue-500/15 text-blue-600 border-blue-500/30' : 'bg-muted text-muted-foreground border-transparent'}`}>
                  {g.recursive ? 'Recursivo' : 'Só esta'}
                </button>
                <button onClick={() => saveFolderGrant({ ...g, canUpload: !g.canUpload })}
                  title={g.canUpload ? 'Pode enviar arquivos' : 'Somente leitura'}
                  className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${g.canUpload ? 'bg-green-500/15 text-green-600 border-green-500/30' : 'bg-muted text-muted-foreground border-transparent'}`}>
                  {g.canUpload ? 'Envio' : 'Leitura'}
                </button>
                <button onClick={() => removeFolderGrant(g.folderPath)} title="Remover acesso"
                  className="p-1 rounded text-muted-foreground hover:text-red-500 shrink-0">
                  <LuTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {pickerOpen && (
        <FolderPicker
          authenticatedFetch={authenticatedFetch}
          existing={folderGrants.map(g => g.folderPath)}
          onClose={() => setPickerOpen(false)}
          onSelect={(folderPath) => { setPickerOpen(false); saveFolderGrant({ folderPath, recursive: true, canUpload: false }) }}
        />
      )}

      {/* Dispositivos - Grid 2 colunas com itens compactos em linha */}
      <div className="flex-1 px-4 py-4 overflow-hidden flex flex-col">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3 flex-shrink-0">
          <FiMessageCircle className="w-4 h-4" />
          DISPOSITIVOS WHATSAPP
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 flex-1 content-start">
          {devices.map((device) => {
            const perm = localPerms.devicePermissions.find(p => p.device_id === device.id)
            const hasAccess = perm?.can_view || false
            const isMeta = device.api_type === 'meta'
            const phoneDisplay = device.display_phone || device.phone_number || ''

            return (
              <div
                key={device.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${
                  hasAccess ? 'border-green-500/30 bg-green-500/5' : 'neu-card border-border bg-muted/10'
                }`}
              >
                {/* Avatar compacto */}
                <div className="relative flex-shrink-0">
                  {device.profile_pic_url ? (
                    <img src={device.profile_pic_url} alt={device.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-xs">
                      {getInitials(device.name)}
                    </div>
                  )}
                  <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background ${
                    device.status === 'connected' || device.status === 'open' ? 'bg-green-400' : 'bg-gray-400'
                  }`} />
                </div>

                {/* Badge API */}
                {isMeta ? (
                  <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">
                    <MetaIcon className="w-3 h-3" />
                  </span>
                ) : (
                  <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-500">
                    <WhatsAppIcon className="w-3 h-3" />
                  </span>
                )}

                {/* Nome e telefone */}
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm truncate block">{device.name}</span>
                  {phoneDisplay && <span className="text-xs text-muted-foreground">{phoneDisplay}</span>}
                </div>

                {/* Permissões inline quando ativo */}
                {hasAccess && (
                  <div className="flex gap-0.5 flex-shrink-0">
                    <PermIcon icon={<LuEye className="w-3 h-3" />} active={perm?.can_view} onClick={() => handleDevicePermission(device.id, { canView: !perm?.can_view, canChat: perm?.can_chat, canEdit: perm?.can_edit, canBlast: perm?.can_blast, canAdmin: perm?.can_admin })} title="Ver" />
                    <PermIcon icon={<LuMessageSquare className="w-3 h-3" />} active={perm?.can_chat} onClick={() => handleDevicePermission(device.id, { canView: perm?.can_view, canChat: !perm?.can_chat, canEdit: perm?.can_edit, canBlast: perm?.can_blast, canAdmin: perm?.can_admin })} title="Chat" />
                    <PermIcon icon={<LuPencil className="w-3 h-3" />} active={perm?.can_edit} onClick={() => handleDevicePermission(device.id, { canView: perm?.can_view, canChat: perm?.can_chat, canEdit: !perm?.can_edit, canBlast: perm?.can_blast, canAdmin: perm?.can_admin })} title="Editar" />
                    <PermIcon icon={<LuSend className="w-3 h-3" />} active={perm?.can_blast} onClick={() => handleDevicePermission(device.id, { canView: perm?.can_view, canChat: perm?.can_chat, canEdit: perm?.can_edit, canBlast: !perm?.can_blast, canAdmin: perm?.can_admin })} title="Blast" />
                    <PermIcon icon={<LuUserCog className="w-3 h-3" />} active={perm?.can_admin} onClick={() => handleDevicePermission(device.id, { canView: perm?.can_view, canChat: perm?.can_chat, canEdit: perm?.can_edit, canBlast: perm?.can_blast, canAdmin: !perm?.can_admin })} title="Admin" />
                  </div>
                )}

                {/* Botão adicionar/remover */}
                <button
                  onClick={() => hasAccess ? handleDevicePermission(device.id, null) : handleDevicePermission(device.id, { canView: true, canChat: true })}
                  className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                    hasAccess
                      ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                      : 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                  }`}
                >
                  {hasAccess ? <LuX className="w-3.5 h-3.5" /> : <LuPlus className="w-3.5 h-3.5" />}
                </button>
              </div>
            )
          })}
          {devices.length === 0 && <p className="text-sm text-muted-foreground text-center py-4 col-span-2">Nenhum dispositivo cadastrado</p>}
        </div>
      </div>
    </div>
  )
}

// Ícone de permissão compacto
function PermIcon({ icon, active, onClick, title }: { icon: React.ReactNode; active?: boolean; onClick: () => void; title: string }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1 rounded transition-colors ${
        active ? 'bg-green-500/30 text-green-600' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
      }`}
    >
      {icon}
    </button>
  )
}

// Painel de criação de usuário (abre no flip)
export function CreateUserPanel({
  onClose,
  onUserCreated,
  authenticatedFetch
}: {
  onClose: () => void
  onUserCreated?: () => void
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
}) {
  const [form, setForm] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
    isAdmin: false
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.username || !form.password) {
      setError('Username e senha são obrigatórios')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const response = await authenticatedFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(form)
      })
      const data = await response.json()

      if (data.success) {
        setSuccess(true)
        // Aguarda um momento para mostrar o sucesso e depois fecha
        setTimeout(() => {
          if (onUserCreated) onUserCreated()
          onClose()
        }, 1000)
      } else {
        setError(data.error || 'Erro ao criar usuário')
      }
    } catch {
      setError('Erro ao criar usuário')
    }
    setSaving(false)
  }

  return (
    <div className="h-full flex flex-col bg-background neu-flat">
      {/* Header */}
      <div className="neu-flat flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <LuUserPlus className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="font-semibold text-base">Novo Usuário</div>
            <div className="text-sm text-muted-foreground">Cadastrar funcionário</div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-muted rounded-md">
          <LuX className="w-5 h-5" />
        </button>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="flex-1 p-4 space-y-4">
        {success && (
          <div className="px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-md text-green-500 text-sm flex items-center gap-2">
            <LuCheck className="w-4 h-4" />
            Usuário criado com sucesso!
          </div>
        )}
        {error && (
          <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-md text-red-500 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Username *</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="usuario.exemplo"
              required
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Senha *</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="Senha inicial"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Nome completo</label>
            <input
              type="text"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="Nome Sobrenome"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              placeholder="email@exemplo.com"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isAdmin"
            checked={form.isAdmin}
            onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })}
            className="w-4 h-4 rounded"
          />
          <label htmlFor="isAdmin" className="text-sm cursor-pointer flex items-center gap-2">
            <LuShield className="w-4 h-4 text-amber-500" />
            Administrador
          </label>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-border rounded-md text-sm hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <>
                <LuCheck className="w-4 h-4" />
                Criar Usuário
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

// Seletor de pasta: navega a árvore real dos shares (admin) e devolve o path.
function FolderPicker({ authenticatedFetch, existing, onClose, onSelect }: {
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
  existing: string[]
  onClose: () => void
  onSelect: (path: string) => void
}) {
  const [path, setPath] = useState('')
  const [dirs, setDirs] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const segments = path ? path.split('/') : []

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    authenticatedFetch(`/api/files/list?path=${encodeURIComponent(path)}`)
      .then(r => r.json())
      .then(d => { if (cancelled) return; if (!d.ok) throw new Error(d.error || 'erro'); setDirs((d.entries || []).filter((e: { isDir: boolean }) => e.isDir).map((e: { name: string }) => e.name)) })
      .catch(e => { if (!cancelled) setError(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [path, authenticatedFetch])

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-background shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <LuFolder className="w-5 h-5 text-amber-400" />
          <span className="flex-1 font-medium text-sm">Selecionar pasta para liberar</span>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><LuX className="w-4 h-4" /></button>
        </div>
        {/* breadcrumb */}
        <div className="flex items-center gap-1 px-4 py-2 text-sm border-b border-border overflow-x-auto">
          <button onClick={() => setPath('')} className="inline-flex items-center text-muted-foreground hover:text-foreground"><LuHouse className="w-4 h-4" /></button>
          {segments.map((seg, i) => (
            <span key={i} className="inline-flex items-center gap-1 min-w-0">
              <LuChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <button onClick={() => setPath(segments.slice(0, i + 1).join('/'))}
                className={`truncate max-w-[120px] ${i === segments.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>{seg}</button>
            </span>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto min-h-[200px]">
          {loading && <div className="py-10 text-center text-sm text-muted-foreground">Carregando…</div>}
          {error && <div className="m-4 px-3 py-2 rounded-lg bg-red-500/10 text-red-500 text-sm">{error}</div>}
          {!loading && !error && dirs.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">Nenhuma subpasta aqui.</div>}
          <ul className="divide-y divide-border/40">
            {dirs.map(name => {
              const childPath = path ? `${path}/${name}` : name
              const already = existing.includes(childPath)
              return (
                <li key={name} className="flex items-center gap-2 px-4 py-2.5 hover:bg-muted/40">
                  <button onClick={() => setPath(childPath)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                    <LuFolder className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="truncate text-sm">{name}</span>
                  </button>
                  {already
                    ? <span className="text-[11px] text-green-600 shrink-0">já liberada</span>
                    : <button onClick={() => onSelect(childPath)} className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 shrink-0">Liberar</button>}
                </li>
              )
            })}
          </ul>
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground truncate">{path ? `Pasta atual: ${path}` : 'Escolha uma pasta (não dá pra liberar a raiz)'}</span>
          <button disabled={!path || existing.includes(path)} onClick={() => onSelect(path)}
            className="px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50 shrink-0">
            Liberar esta pasta
          </button>
        </div>
      </div>
    </div>
  )
}

